import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AskComposer,
  Button,
  Card,
  DockFoot,
  DockGrip,
  DockHead,
  DockHistory,
  DockPanel,
  DockPin,
  DockRailLabel,
  DockThreadRow,
  EmptyState,
  ErrorState,
  Icon,
  IconButton,
  Inline,
  LoadingState,
  Message,
  MessageAttachments,
  Orb,
  Suggestions,
  Thread,
  ThreadNotice,
} from '../ds'
import { ICON_PATHS } from './icons'
import { clampDockWidth } from './dockPreference'
import { suggestionsFor, type AskSurface } from './askSuggestions'
import { handoffFor } from './askHandoff'
import { toContextHint } from './askContext'
import type { AskThread } from './useAskThread'
import { COUNT_FROM_CHARS, MAX_MESSAGE_CHARS, clippedCopy } from './askLimits'
import type { AskEntity, AskEntityType } from './ShellContext'
import { AssistantMarkdown, PriorSourceCardList, ProposalCard, SourceCardList, ToolOutputRenderer } from '../components/chat'

/**
 * The Ask dock — Marva's persistent column on the right of the shell
 * (`Prototype - Three Surfaces v6.dc.html`, lines 1915–2083).
 *
 * **One deliberate departure from the prototype.** There the dock is a flex
 * sibling of `.mv-app` inside a `.shell` wrapper, and its width is written to
 * `.askdock` as an inline style on every update — which defeats the
 * prototype's own media queries (the ≤900px overlay never gets its
 * `min(360px,86vw)`, and the drawer-scrim gutter stays 400px wide at phone
 * width). Here the dock is a real grid column on `.mv-app`, `--dockw` is the
 * only width channel, and the band is decided in JS by `useBreakpoint` — so one
 * value drives the track and nothing has to out-specify a media query.
 *
 * What this component does NOT own: layout state is `useDockLayout` and the
 * conversation is `useAskThread`, both held by `AppShell`. The grid track that
 * sizes this column lives on `.mv-app`, and the conversation has to be scoped
 * to the live workspace at the read — neither belongs to a component that can
 * unmount.
 */

/** §3.2: `Reading the Ledger/Knowledge/Authority/Value with you`. */
const SURFACE_LABEL: Record<AskSurface, string> = {
  ledger: 'the Ledger',
  knowledge: 'Knowledge',
  authority: 'Authority',
  value: 'Value',
}

/**
 * §3.2 names three (`DECISION`, `CUSTOMER`, `PRODUCT`). The channel carries two
 * more, and an unlabelled pin is not an option, so they get the obvious reading
 * of their own type — flagged here so the next editor knows which two the
 * design actually specified.
 *
 * ⚠️ These are upper-case because §3.2 writes them that way and the strings are
 * the acceptance contract; v6's notification pin is sentence case for the same
 * reason. The pin's `Eyebrow` is `text-transform:uppercase`, so both render
 * identically — the casing difference is in the sources, not in the product.
 */
const ENTITY_KIND: Record<AskEntityType, string> = {
  decision: 'READING THIS DECISION',
  customer: 'READING THIS CUSTOMER',
  product: 'READING THIS PRODUCT',
  authority_exception: 'READING THIS EXCEPTION',
  attribution_order: 'READING THIS ORDER',
}

/** Short relative time for a thread row (just now / 2h ago / yesterday …). */
function relativeTime(iso?: string): string {
  if (!iso) return ''
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const min = Math.floor((Date.now() - then) / 60000)
  if (min < 1) return 'just now'
  if (min < 60) return `${min}m ago`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}h ago`
  const day = Math.floor(hr / 24)
  if (day === 1) return 'yesterday'
  if (day < 7) return `${day} days ago`
  return new Date(iso).toLocaleDateString()
}

export interface AskDockProps {
  collapsed: boolean
  /** Drawn as a fixed overlay rather than a grid column (under 900px). */
  overlay: boolean
  /**
   * The collapsed rail stands down. Only in the overlay band, and only while a
   * drawer is open: `--dockw` is 0 there, so the drawer runs to the viewport
   * edge and its close button lands under the rail.
   */
  behindDrawer: boolean
  /** The merchant collapsing or expanding it by hand. */
  onCollapsedChange: (collapsed: boolean) => void
  /** The grip, mid-drag — already clamped. */
  onWidthChange: (px: number) => void
  /**
   * The widest the dock may be in this window — the design's 640 ceiling, or
   * what is left after the rail, the context panel and a usable canvas.
   */
  maxWidth: number
  /**
   * Whether to offer the grip at all. ⚠️ False in the overlay band: the dock is
   * `position:fixed` at `min(360px,86vw)` there, so dragging would store a
   * width nothing ever applies — a control that visibly does nothing.
   */
  resizable: boolean
  /**
   * Bumped by ⌘K, the rail and a drawer's "Ask" button. Any
   * change focuses the composer. A nonce rather than a boolean because two
   * presses are two requests to focus, and because the expand and the bump land
   * in the same React commit — by the time the effect runs the composer is
   * mounted, so no timer is needed (the prototype used a 30ms `setTimeout`).
   */
  focusNonce: number
  /**
   * A question a screen asked to put in the composer (`openAsk(draft)`), with a
   * nonce so asking for the same text twice fills it twice. Filled, never sent:
   * the merchant presses send, and `ask` builds that turn's hint then. Never over
   * text the merchant typed (see the effect that applies it).
   */
  draft?: { text: string; nonce: number } | null
  /** Which of §3.2's surfaces the merchant is on, or `null` for the rest. */
  surface: AskSurface | null
  /**
   * The record they have open, already scoped to the live workspace. Drives the
   * pin banner AND the suggestions.
   */
  entity: AskEntity | null
  /**
   * What the open Inbox is showing, or `null` when it is shut. The Inbox has no
   * single record to publish as an entity, but it IS a drawer in front of the
   * merchant, so it produces a pin of its own — and it takes precedence, as in
   * v6, because it is the thing they just opened.
   *
   * It does not steer the suggestions: there is nothing to key them on beyond
   * the surface, and both the Ledger and Authority sets already offer the
   * pending-queue question that `list_attention_items` answers.
   */
  inboxLabel: string | null
  thread: AskThread
}

export default function AskDock({
  collapsed,
  overlay,
  behindDrawer,
  onCollapsedChange,
  onWidthChange,
  maxWidth,
  resizable,
  focusNonce,
  draft,
  surface,
  entity,
  inboxLabel,
  thread,
}: AskDockProps) {
  const navigate = useNavigate()
  const [view, setView] = useState<'thread' | 'history'>('thread')
  const [input, setInput] = useState('')
  /**
   * A screen's draft was cut to fit the limit. Said under the composer, never done
   * silently. A paste or keystroke over the cap is `AskComposer`'s own to cut and say.
   */
  const [clipped, setClipped] = useState(false)
  /**
   * Remounts the composer, clearing its own "your paste was cut" note, when the
   * text is replaced from OUTSIDE it (a suggestion, a new thread). A send from the
   * composer clears the note itself and keeps focus, so it does not bump this.
   */
  const [composerKey, setComposerKey] = useState(0)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const rootRef = useRef<HTMLElement>(null)
  const streamRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (focusNonce === 0) return
    inputRef.current?.focus()
  }, [focusNonce])

  /**
   * A screen's draft fills the composer ONLY when the merchant has nothing of their own in
   * it: empty, whitespace, or still exactly the last draft a screen put there (untouched, so
   * a second chip replaces the first). ⚠️ Anything they typed is kept — a chip click must
   * never erase it. The dock opens and focuses either way (`openAsk` does both).
   *
   * `inputNow` and `lastDraft` are refs, synced in the effect declared FIRST, so the draft
   * effect reads the composer as it stands in the same commit.
   */
  const inputNow = useRef(input)
  const lastDraft = useRef<string | null>(null)
  useEffect(() => {
    inputNow.current = input
  })
  useEffect(() => {
    if (!draft) return
    setView('thread')
    const cur = inputNow.current
    if (cur.trim() && cur !== lastDraft.current) return
    const text = draft.text.slice(0, MAX_MESSAGE_CHARS)
    lastDraft.current = text
    setInput(text)
    setClipped(draft.text.length > MAX_MESSAGE_CHARS)
  }, [draft])

  useEffect(() => {
    streamRef.current?.scrollTo?.({ top: streamRef.current.scrollHeight, behavior: 'smooth' })
  }, [thread.messages, thread.sending])

  /**
   * Escape collapses the dock — and, when focus is inside the dock, does ONLY
   * that.
   *
   * ⚠️ Five other Escape handlers are bound at the same time (`AppShell`,
   * `InboxDrawer.tsx:110`, Knowledge's two drawers, `Journal`'s,
   * `ConfigPanel`'s), and one Escape used to fire every one of them — so
   * pressing Escape in the composer collapsed the dock AND closed the Inbox.
   * Two unrelated things from one key is not a tangle to tolerate; it is this
   * feature's own bug.
   *
   * ⚠️ Registered on the **capture** phase of the WINDOW, and it stops the event
   * dead. That is deliberate and it is not the same as registering first: the
   * design system's one Escape stack (Drawer, Modal — overlays.md) is itself a
   * capture listener on `document`, and window capture runs before every
   * document listener regardless of the order effects happened to run in. So
   * with the Inbox open and focus in the composer, Escape collapses the dock and
   * the Inbox stays — the dock is "captured first", as overlays.md says.
   */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      const root = rootRef.current
      const active = document.activeElement
      if (!root || !active || !root.contains(active)) return
      e.stopImmediatePropagation()
      onCollapsedChange(true)
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [onCollapsedChange])

  /**
   * The left-edge resize grip. Pointer events, with move/up on `window` so a
   * drag that outruns the 7px handle keeps resizing, and with every listener
   * (and the text-selection block) removed on pointerup or pointercancel.
   */
  const onGripDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault()
      const grip = e.currentTarget
      const pointerId = e.pointerId
      grip.setPointerCapture?.(pointerId)

      const move = (ev: PointerEvent) =>
        // Clamped to what will actually fit as well as to the design's bounds,
        // so dragging past the point where the canvas would be squeezed out is
        // not a dead zone that silently does nothing.
        onWidthChange(Math.min(maxWidth, clampDockWidth(window.innerWidth - ev.clientX)))
      const stop = () => {
        window.removeEventListener('pointermove', move)
        window.removeEventListener('pointerup', stop)
        window.removeEventListener('pointercancel', stop)
        document.body.style.userSelect = ''
        grip.releasePointerCapture?.(pointerId)
      }

      document.body.style.userSelect = 'none'
      window.addEventListener('pointermove', move)
      window.addEventListener('pointerup', stop)
      window.addEventListener('pointercancel', stop)
    },
    [maxWidth, onWidthChange]
  )

  /**
   * Send, with the hint built HERE rather than inside `useAskThread`.
   *
   * ⚠️ This is the per-turn capture §3.2 asks for. `surface` and `entity` are
   * props of the render this handler belongs to, so the hint describes the
   * screen at the moment the merchant pressed send — not the screen the reply
   * arrives on. Navigating during a slow turn therefore changes the NEXT turn's
   * context and rewrites nothing, which is the rule stated rather than hoped
   * for.
   *
   * ⚠️ And it goes through `toContextHint`, never built inline: that converter
   * is the one place that drops `label` (which can carry a customer's email
   * address) and `workspaceId` from the shell pin. Building a hint by hand here
   * would put both back on the wire and 400 every turn.
   */
  const ask = useCallback(
    (text: string, fromComposer = false, retryOf?: string) => {
      // Over the limit is not sent at all: the server would 400 it. The
      // composer cannot normally get here (it is capped), so this is the guard
      // for anything that bypasses it.
      if (!text.trim() || thread.sending || text.length > MAX_MESSAGE_CHARS) return
      // ⚠️ Try again (D-38) leaves the composer alone: the merchant may already
      // be typing their next question, and the retried text is the failed
      // message's own, not the composer's.
      if (retryOf === undefined) {
        setInput('')
        setClipped(false)
        if (!fromComposer) setComposerKey((k) => k + 1)
      } else {
        // The retry removes the failed message and its notice — Try again included — so
        // focus would fall to <body>. It goes to the composer, where the merchant's next
        // keystroke belongs.
        inputRef.current?.focus()
      }
      setView('thread')
      // The hint is built at the press, retry included: a retry is a new turn
      // asked from whatever screen is open now.
      void thread.send(text, toContextHint(surface, entity), retryOf ? { retryOf } : undefined).then((outcome) => {
        // The server refused it for length (a limit that moved, or a bypass):
        // hand the merchant their text back to trim, unless they have already
        // started typing something else.
        if (outcome === 'too_long') setInput((current) => (current === '' ? text : current))
      })
    },
    [thread, surface, entity]
  )

  const frame = { ref: rootRef, collapsed, overlay, behind: behindDrawer, 'aria-label': 'Ask Marva' }

  if (collapsed) {
    return (
      <DockPanel {...frame}>
        <IconButton
          variant="outline"
          size="lg"
          label="Ask her — ⌘K"
          icon={<Orb size={17} />}
          onClick={() => onCollapsedChange(false)}
        />
        <DockRailLabel>Ask Marva</DockRailLabel>
      </DockPanel>
    )
  }

  const showHistory = view === 'history'
  // Whatever is open in front of the merchant. The Inbox first — it is the
  // thing they just opened, and v6 orders it the same way — then the record a
  // drawer published.
  const pin = inboxLabel
    ? { kind: 'Reading your notifications', label: inboxLabel }
    : entity
      ? { kind: ENTITY_KIND[entity.type], label: entity.label }
      : null
  // §3.2's two context strings. Anything open beats the page it sits on.
  const contextLine = pin
    ? 'She can see what you opened'
    : `Reading ${surface ? SURFACE_LABEL[surface] : 'the app'} with you`
  const suggestions = suggestionsFor(surface, entity)
  // The hand-off's two gates (see where it renders): the newest Marva answer only, and
  // never while that answer's proposal can still be confirmed (or is being acted on).
  let newestAnswer = -1
  for (let k = thread.messages.length - 1; k >= 0; k--) {
    if (thread.messages[k].role !== 'user') {
      newestAnswer = k
      break
    }
  }
  const proposalPending = (m: AskThread['messages'][number]) => {
    const live = thread.liveProposal
    return !!m.proposal && !!live && live.receiptId === m.proposal.receiptId && (live.actionable || live.working)
  }

  return (
    <DockPanel {...frame}>
      {resizable && <DockGrip onPointerDown={onGripDown} />}

      <DockHead title={showHistory ? 'Your threads' : 'Ask Marva'} context={contextLine}>
        <IconButton
          size="sm"
          label="New thread"
          icon={<Icon d={ICON_PATHS.plus} />}
          onClick={() => {
            thread.newThread()
            setInput('')
            setClipped(false)
            setComposerKey((k) => k + 1)
            setView('thread')
          }}
        />
        <IconButton
          size="sm"
          label={showHistory ? 'Back to the thread' : 'Your threads'}
          pressed={showHistory}
          icon={<Icon d={ICON_PATHS.clock} />}
          onClick={() => setView(showHistory ? 'thread' : 'history')}
        />
        <IconButton
          size="sm"
          label="Collapse"
          icon={<Icon d={ICON_PATHS.chevronRight} />}
          onClick={() => onCollapsedChange(true)}
        />
      </DockHead>

      {showHistory ? (
        <DockHistory>
          {thread.threadsState === 'error' ? (
            // §3.4: a read that failed must never be presented as "nothing
            // exists". An empty-looking history would tell the merchant their
            // threads are gone.
            <ErrorState title="I couldn’t read your threads just now.">
              They are still there — try again in a moment.
            </ErrorState>
          ) : thread.threads.length === 0 ? (
            thread.threadsState === 'ready' ? (
              <EmptyState size="sm">Nothing yet. Every thread you start is kept here.</EmptyState>
            ) : (
              <LoadingState caption="Looking for your threads…" />
            )
          ) : (
            thread.threads.map((t) => (
              // The row truncates the title to one line and shows it in full on hover.
              <DockThreadRow
                key={t.conversationId}
                title={t.title}
                time={relativeTime(t.updatedAt)}
                current={t.conversationId === thread.conversationId}
                onClick={() => {
                  thread.openThread(t.conversationId)
                  setView('thread')
                }}
              />
            ))
          )}
        </DockHistory>
      ) : (
        <>
          {pin && <DockPin kind={pin.kind} label={pin.label} />}

          <Thread threadRef={streamRef} busy={thread.sending || thread.opening}>
            {thread.openFailed && (
              // §3.4 again: a read that failed is not a result. Without this the
              // merchant landed on the PREVIOUS thread's transcript believing
              // it was the one they clicked.
              <ThreadNotice tone="info">
                I couldn&rsquo;t open that thread just now. It is still there — try again in a moment.
              </ThreadNotice>
            )}
            {thread.messages.map((m, i) =>
              m.role === 'user' ? (
                <Fragment key={m.id}>
                  {/* D-38: a turn that did not land is the merchant's own
                      message, dashed, with a notice under it — never a Marva
                      message. Try again only where retrying can help, and only
                      on the newest turn: resending an older one would ask it
                      again out of order. */}
                  <Message from="user" failed={!!m.failure}>
                    {m.content}
                  </Message>
                  {m.failure && (
                    <ThreadNotice
                      onRetry={
                        m.failure.retryable && i === thread.messages.length - 1 && !thread.sending
                          ? () => ask(m.content, false, m.id)
                          : undefined
                      }
                    >
                      {m.failure.copy}
                    </ThreadNotice>
                  )}
                </Fragment>
              ) : (
                <Fragment key={m.id}>
                  <Message from="marva">
                    <AssistantMarkdown content={m.content} />
                  </Message>
                  {/* What the answer read, as typed cards built by the server's
                      code (MAR-173 PR 3) — never parsed out of the reply text. */}
                  {m.sources && m.sources.length > 0 && (
                    <MessageAttachments>
                      <SourceCardList cards={m.sources} />
                    </MessageAttachments>
                  )}
                  {/* A change she PREPARED (MAR-175) — server data only. Live
                      only when it is the hook's scoped live proposal; a
                      reloaded or older one renders inert. Only the card's
                      Confirm button reaches the confirm call — a typed "yes"
                      is just another message. */}
                  {m.proposal && (
                    <MessageAttachments>
                      <ProposalCard
                        proposal={m.proposal}
                        live={
                          thread.liveProposal?.receiptId === m.proposal.receiptId ? thread.liveProposal : null
                        }
                        onConfirm={() => void thread.actOnProposal(m.proposal!.receiptId, 'confirm')}
                        onCancel={() => void thread.actOnProposal(m.proposal!.receiptId, 'cancel')}
                      />
                    </MessageAttachments>
                  )}
                  {/* The hand-off (v6): where the rest of this answer lives, as one
                      button. Built from the answer's own source cards by code
                      (`askHandoff.ts`) — a surface that shows what it read, never a
                      filter or record it cannot open. The cards' own links stay; this
                      is the one the design puts last. Under 900px the dock covers the
                      canvas, so it steps aside there or the merchant would see nothing
                      change. On the NEWEST answer only — a column of identical buttons
                      down the thread says nothing — and never beside a proposal that
                      can still be confirmed: under 900px the tap would collapse the
                      dock away from its pending Confirm (PR #321 review). */}
                  {i === newestAnswer && !proposalPending(m) && (() => {
                    const handoff = handoffFor(m.sources)
                    if (!handoff) return null
                    return (
                      <MessageAttachments>
                        <Card tone="gold" padding="sm">
                          <Inline justify="between">
                            <span className="t-small">{handoff.note}</span>
                            <Button
                              variant="gold"
                              size="sm"
                              onClick={() => {
                                navigate(handoff.to)
                                if (overlay) onCollapsedChange(true)
                              }}
                            >
                              {handoff.label}
                            </Button>
                          </Inline>
                        </Card>
                      </MessageAttachments>
                    )
                  })()}
                  {/* Earlier reads that were in front of Marva when she
                      answered (MAR-173 replay): labelled with when they were
                      read, and kept OUT of the answer's own sources. Not a
                      claim they were used — the server attaches them to any
                      answered turn that read nothing and was sent the replay. */}
                  {m.priorSources && m.priorSources.length > 0 && (
                    <MessageAttachments prior>
                      <PriorSourceCardList cards={m.priorSources} />
                    </MessageAttachments>
                  )}
                  {/* ⚠️ Legacy: only a reloaded turn written before PR 3 carries
                      fence-parsed `toolOutputs`. `useAskThread` maps it on reload
                      and never on send, and a turn with cards does not draw both. */}
                  {!(m.sources && m.sources.length > 0) && m.toolOutputs && m.toolOutputs.length > 0 && (
                    <MessageAttachments>
                      <ToolOutputRenderer outputs={m.toolOutputs} />
                    </MessageAttachments>
                  )}
                </Fragment>
              )
            )}
            {(thread.sending || thread.opening) && <Message pending />}
          </Thread>

          {/* Two states, as in v6: the suggestions stay through the thread,
              and the label says whether they are a way in or a way on. */}
          <Suggestions
            items={suggestions}
            label={thread.messages.length === 0 ? 'Try asking' : 'Follow on'}
            disabled={thread.sending}
            onPick={(label) => ask(label)}
          />
        </>
      )}

      {/* The composer sits below both views, as in the design — the merchant
          can start typing without leaving the thread list first. A textarea
          growing 1–6 lines: Enter asks, Shift+Enter adds a line (D-40). */}
      <DockFoot>
        <AskComposer
          key={composerKey}
          value={input}
          onChange={(next) => {
            setInput(next)
            if (next.length < MAX_MESSAGE_CHARS) setClipped(false)
          }}
          onSubmit={(text) => ask(text, true)}
          placeholder="Ask about a customer, a product, a number…"
          label="Ask Marva"
          maxChars={MAX_MESSAGE_CHARS}
          countFrom={COUNT_FROM_CHARS}
          busy={thread.sending}
          clipped={clipped ? true : undefined}
          clippedNote={clippedCopy()}
          inputRef={inputRef}
        />
      </DockFoot>
    </DockPanel>
  )
}
